import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'

import { asyncJobShape } from '../async-jobs/models'
import type { BulkSelectionPayload } from 'src/hooks/use-bulk-selection'
import {
  expenseFilterShape,
  expenseGroupCreateShape,
  expenseGroupShape,
  retencionTypeEnum,
} from './models'

/** camelCase → snake_case, preserving existing `__lookup` suffixes (e.g. `expenseDate__gte`). */
const toSnakeKey = (key: string) => key.replace(/([A-Z])/g, '_$1').toLowerCase()

/**
 * Serialize a select-all filter snapshot into the exact shape the expense
 * filterset reads from query params: snake_cased keys, arrays comma-joined
 * (the list's axios instance uses `arrayFormat: 'comma'`, and the MultiValue
 * filters split on commas), falsy/empty values dropped. This keeps the
 * server-resolved select-all set identical to what the list showed.
 */
const serializeBulkFilters = (filters: Record<string, unknown>): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      out[toSnakeKey(key)] = value.join(',')
    } else {
      out[toSnakeKey(key)] = String(value)
    }
  }
  return out
}

const customCreate = createCustomServiceCall({
  inputShape: expenseGroupCreateShape,
  outputShape: expenseGroupShape,

  cb: async ({ client, input, utils }) => {
    const data = utils.toApi(input)
    const response = await client.post('/expenses/expense-group/', data)
    return utils.fromApi(response.data)
  },
})

const regenerateReceipt = createCustomServiceCall({
  inputShape: { id: z.string() },
  outputShape: expenseGroupShape,
  cb: async ({ client, input, utils, slashEndingBaseUri }) => {
    const response = await client.get(`${slashEndingBaseUri}${input.id}/regenerate-receipt/`)
    return utils.fromApi(response.data)
  },
})

const listCategoryOpts = createCustomServiceCall({
  inputShape: { country: z.string().optional() },
  outputShape: z.array(
    z.object({
      id: z.string(),
      value: z.string(),
      label: z.string(),
    }),
  ),
  cb: async ({ client, input, utils }) => {
    const params = input.country ? { country: input.country } : undefined
    const response = await client.get('/expenses/category-options/', { params })
    return utils.fromApi(response.data)
  },
})

const listCashFlowTypeOpts = createCustomServiceCall({
  outputShape: z.array(z.tuple([z.string(), z.string()])),
  cb: async ({ client, utils }) => {
    const response = await client.get('/expenses/cash-flow-type/')
    return utils.fromApi(response.data)
  },
})

const listTransactionTypeOpts = createCustomServiceCall({
  outputShape: z.array(z.tuple([z.string(), z.string()])),
  cb: async ({ client, utils }) => {
    const response = await client.get('/expenses/transaction-type-options/')
    return utils.fromApi(response.data)
  },
})

const copyAndSplit = createCustomServiceCall({
  inputShape: {
    id: z.string().uuid(),
    amount: z.number(),
    name: z.string().optional(),
    organization: z.string().uuid().optional(),
    reports: z.array(z.string().uuid()).optional(),
    notes: z.string().optional(),
    copyReceipts: z.boolean().optional(),
  },
  outputShape: {
    original: z.object(expenseGroupShape),
    new: z.object(expenseGroupShape),
  },
  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const { id, ...body } = input
    const response = await client.post(`${slashEndingBaseUri}${id}/copy-and-split/`, {
      amount: body.amount,
      ...(body.name !== undefined && { name: body.name }),
      ...(body.organization !== undefined && { organization: body.organization }),
      ...(body.reports !== undefined && { reports: body.reports }),
      ...(body.notes !== undefined && { notes: body.notes }),
      ...(body.copyReceipts !== undefined && { copy_receipts: body.copyReceipts }),
    })
    return utils.fromApi(response.data)
  },
})

const generateCuentaDeCobro = createCustomServiceCall({
  inputShape: {
    id: z.string(),
    retencionType: z.enum(retencionTypeEnum),
    retencionValue: z.number(),
  },
  outputShape: { asyncJobId: z.string().uuid() },
  cb: async ({ client, input, slashEndingBaseUri, utils }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.id}/generate-cuenta-de-cobro/`,
      {
        retencion_type: input.retencionType,
        retencion_value: input.retencionValue,
      },
    )
    return utils.fromApi(response.data)
  },
})

const bulkDelete = createCustomServiceCall({
  inputShape: { payload: z.any() },
  outputShape: asyncJobShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const payload = input.payload as BulkSelectionPayload<Record<string, unknown>>
    const body =
      'select_all' in payload
        ? { ...payload, filters: serializeBulkFilters(payload.filters ?? {}) }
        : payload
    const response = await client.post(`${slashEndingBaseUri}bulk-delete/`, body)
    return fromApi(response.data)
  },
})

const bulkAddToReport = createCustomServiceCall({
  inputShape: { payload: z.any(), report: z.string().uuid() },
  outputShape: asyncJobShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const payload = input.payload as BulkSelectionPayload<Record<string, unknown>>
    const selection =
      'select_all' in payload
        ? { ...payload, filters: serializeBulkFilters(payload.filters ?? {}) }
        : payload
    const response = await client.post(`${slashEndingBaseUri}bulk-add-to-report/`, {
      ...selection,
      report: input.report,
    })
    return fromApi(response.data)
  },
})

const bulkCategorize = createCustomServiceCall({
  inputShape: { payload: z.any(), expenseCategory: z.string().uuid() },
  outputShape: asyncJobShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const payload = input.payload as BulkSelectionPayload<Record<string, unknown>>
    const selection =
      'select_all' in payload
        ? { ...payload, filters: serializeBulkFilters(payload.filters ?? {}) }
        : payload
    const response = await client.post(`${slashEndingBaseUri}bulk-categorize/`, {
      ...selection,
      expense_category: input.expenseCategory,
    })
    return fromApi(response.data)
  },
})

export const expenseApi = createApi({
  client: axiosInstance,
  baseUri: '/expenses/expense-group/',
  models: {
    entity: expenseGroupShape,
    create: expenseGroupCreateShape,
    extraFilters: expenseFilterShape,
  },
  customCalls: {
    customCreate,
    listCashFlowTypeOpts,
    listCategoryOpts,
    listTransactionTypeOpts,
    regenerateReceipt,
    generateCuentaDeCobro,
    copyAndSplit,
    bulkDelete,
    bulkAddToReport,
    bulkCategorize,
  },
})
